import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  query,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs, // [新增] 需要用來一次性抓取使用者列表
  serverTimestamp,
} from "../../config/dataClient";
import { FileText, Plus, List, Calendar as CalIcon } from "lucide-react";
import { db } from "../../config/firebase";
import { APP_ID, ROLES } from "../../config/constants";
import LeaveCalendar from "./leave/LeaveCalendar";
import LeaveForm from "./leave/LeaveForm";
import LeaveList from "./leave/LeaveList";
import { useToast, useConfirm } from "../ui/ToastConfirm";
import { sendLeaveNotification } from "../../utils/leaveNotifications";

export default function LeaveView({ user, userProfile, role }) {
  const [leaves, setLeaves] = useState([]);
  
  // [修改] 將預設視圖從 "list" 改為 "calendar"
  const [viewMode, setViewMode] = useState("calendar"); 
  
  // [新增] 儲存所有員工資料 (包含生日)
  const [users, setUsers] = useState([]);
  const [proxyOptions, setProxyOptions] = useState([]);
  const [proxyOptionsLoading, setProxyOptionsLoading] = useState(true);
  const [proxyEmails, setProxyEmails] = useState({});
  useEffect(() => {
    let cancelled = false;
    Promise.all(proxyOptions.map(async p => {
      try {
        const contact = await getDoc(doc(db, "artifacts", APP_ID, "public", "data", "notification_contacts", `uid-${p.uid}`));
        return [p.uid, contact.data()?.email || ""];
      } catch { return [p.uid, ""]; }
    })).then(entries => { if (!cancelled) setProxyEmails(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [proxyOptions]);
  useEffect(() => onSnapshot(collection(db, "artifacts", APP_ID, "public", "data", "daily_status"), snapshot => {
    setProxyOptions(snapshot.docs.map(d => ({ ...d.data(), uid: d.id }))
      .filter(p => p.role === ROLES.FULLTIME && !p.isHidden && (p.accountStatus || "active") === "active")
      .sort((a, b) => (a.fullName || a.name || a.uid).localeCompare(b.fullName || b.name || b.uid, "zh-TW")));
    setProxyOptionsLoading(false);
  }, () => { setProxyOptions([]); setProxyOptionsLoading(false); }), []);

  const [targetAnnualInfo, setTargetAnnualInfo] = useState({
    used: 0,
    remaining: 0,
    hireDate: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTargetUid, setEditTargetUid] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  const showToast = useToast();
  const confirm = useConfirm();
  const notify = async (leaveId, event) => {
    try {
      const result = await sendLeaveNotification(user, leaveId, event);
      if (result.status === "preview") showToast("預覽通知已記錄");
      else if (result.status !== "skipped-admin") showToast("通知信已寄送");
    } catch (error) {
      alert(`假單已儲存，但通知信未完成：${error.message}。可在假單列表重試寄送。`);
    }
  };

  // 監聽假單資料
  useEffect(() => {
    const q = query(
      collection(db, "artifacts", APP_ID, "public", "data", "leaves"),
      orderBy("startDate", "asc")
    );
    const unsub = onSnapshot(q, (snapshot) => {
      setLeaves(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // [優化] 優先讀取 daily_status 中的冗餘 birthday/fullName，僅在缺欄位時 fallback 取 profile
  useEffect(() => {
    const fetchUsersForBirthday = async () => {
      try {
        const q = query(collection(db, "artifacts", APP_ID, "public", "data", "daily_status"));
        const snapshot = await getDocs(q);

        const promises = snapshot.docs.map(async (d) => {
          const data = d.data();
          if (data.isHidden) return null;

          const uid = data.uid;
          let userData = {
            uid,
            name: data.fullName || data.name,
            birthday: data.birthday || "",
          };

          // 若 daily_status 已有 birthday，無需再 fetch profile（消除 N+1）
          if (userData.birthday) return userData;

          // 舊資料 fallback：去抓 profile 補齊
          try {
            const pSnap = await getDoc(doc(db, "artifacts", APP_ID, "users", uid, "profile", "main"));
            if (pSnap.exists()) {
              const pData = pSnap.data();
              if (pData.isHidden) return null;
              userData.birthday = pData.birthday || "";
              if (pData.fullName) userData.name = pData.fullName;
            }
          } catch (e) {
            console.warn(`無法讀取使用者 ${uid} 的詳細資料 (權限可能不足)`, e);
          }
          return userData;
        });

        const res = await Promise.all(promises);
        setUsers(res.filter((u) => u && u.birthday));
      } catch (err) {
        console.error("抓取員工生日列表失敗:", err);
      }
    };

    fetchUsersForBirthday();
  }, []);

  useEffect(() => {
    const targetUid = isEditing && editTargetUid ? editTargetUid : user?.uid;
    if (!targetUid) return;
    const fetchProfile = async () => {
      const docRef = doc(
        db,
        "artifacts",
        APP_ID,
        "users",
        targetUid,
        "profile",
        "main"
      );
      const snap = await getDoc(docRef);
      if (snap.exists())
        setTargetAnnualInfo({
          used: snap.data().annualLeaveUsed || 0,
          remaining: snap.data().annualLeaveRemaining || 0,
          hireDate: snap.data().startDate || "",
        });
    };
    fetchProfile();
  }, [user, isEditing, editTargetUid]);

  const handleEdit = (leave) => {
    setInitialFormData({
      type: leave.type,
      startDate: leave.startDate,
      endDate: leave.endDate,
      reason: leave.reason,
      proxyEmail: leave.proxyEmail || "",
      proxyUid: leave.proxyUid || "",
      otherType: leave.otherType || "",
      proofImage: leave.proofImage,
    });
    setEditId(leave.id);
    setEditTargetUid(leave.uid);
    setIsEditing(true);
    setViewMode("form");
  };

  const handleDelete = async (leaveId) => {
    const ok = await confirm({
      title: "刪除假單",
      message: "確定要刪除這筆假單嗎？此操作無法復原。",
      confirmText: "刪除",
      danger: true,
    });
    if (!ok) return;
    await deleteDoc(
      doc(db, "artifacts", APP_ID, "public", "data", "leaves", leaveId)
    );
    showToast("已刪除假單");
  };

  // ★★★ 核心修改：如果是編輯模式，保留原本的簽名與狀態 ★★★
  const handleFormSubmit = async (data) => {
    // 1. 如果是編輯模式，先找出原本那張假單的完整資料
    const currentLeave = isEditing && editId 
        ? leaves.find(l => l.id === editId) 
        : null;

    const leaveData = {
      uid: isEditing ? editTargetUid : user.uid,
      userName: isEditing
        ? (currentLeave?.userName || userProfile.name) // 編輯時使用原本的名字，避免跑掉
        : userProfile.name,
      
      // 2. 狀態：如果是編輯，保留原本狀態；如果是新增，才是 pending
      status: isEditing ? (currentLeave?.status || "pending") : "pending",

      // 3. 代簽與主管簽核：
      // 如果是編輯模式，保留原本的簽名；如果是新增，才預設為空字串
      proxySignature: isEditing ? (currentLeave?.proxySignature || "") : "",
      managerSignature: isEditing ? (currentLeave?.managerSignature || "") : "",
      
      // 4. 建立時間：編輯時保留原本時間
      createdAt: isEditing ? (currentLeave?.createdAt || serverTimestamp()) : serverTimestamp(),
      
      // 5. 展開表單資料 (包含新上傳的 proofImage)
      ...data, 
    };

    if (isEditing && editId) {
      await updateDoc(
        doc(db, "artifacts", APP_ID, "public", "data", "leaves", editId),
        leaveData
      );
      showToast("更新成功");
      if (leaveData.status === "pending" && !leaveData.proxySignature) await notify(editId, "submitted");
    } else {
      const createdLeave = await addDoc(
        collection(db, "artifacts", APP_ID, "public", "data", "leaves"),
        leaveData
      );
      showToast("送出成功");
      await notify(createdLeave.id, "submitted");
    }

    setViewMode("list");
    setIsEditing(false);
    setEditId(null);
    setEditTargetUid(null);
    setInitialFormData(null);
  };

  const handleSign = async (leaveId, type) => {
    const leave = leaves.find((l) => l.id === leaveId);
    if (!leave) return;
    if (type === "proxy" && leave.uid === user.uid && role !== ROLES.ADMIN)
      return alert("不能簽署自己的職務代理");

    try {
      const updateData = {};
      if (type !== "proxy") updateData.notificationVersion = Date.now().toString();
      let toastMsg = "已簽名完成";

      if (type === "proxy") {
        updateData.proxySignature = userProfile.name;
        updateData.proxySignerUid = user.uid;
        toastMsg = "已代簽";
      } else if (type === "manager") {
        if (!leave.proxySignature && leave.uid !== user.uid) return alert("需先由職務代理人簽名");
        updateData.managerSignature = userProfile.name;
        updateData.status = "approved";
        updateData.approvalCancelled = false;
        toastMsg = "已核准";

        if (leave.type === "annual") {
          const userRef = doc(db, "artifacts", APP_ID, "users", leave.uid, "profile", "main");
          const userSnap = await getDoc(userRef);
          if (userSnap.exists())
            await updateDoc(userRef, {
              annualLeaveUsed: (userSnap.data().annualLeaveUsed || 0) + leave.days,
              annualLeaveRemaining: (userSnap.data().annualLeaveRemaining || 0) - leave.days,
            });
        }
      } else if (type === "cancel") {
        updateData.managerSignature = "";
        updateData.status = "pending";
        updateData.approvalCancelled = true;
        toastMsg = "已取消核准";

        if (leave.type === "annual") {
          const userRef = doc(db, "artifacts", APP_ID, "users", leave.uid, "profile", "main");
          const userSnap = await getDoc(userRef);
          if (userSnap.exists())
            await updateDoc(userRef, {
              annualLeaveUsed: (userSnap.data().annualLeaveUsed || 0) - leave.days,
              annualLeaveRemaining: (userSnap.data().annualLeaveRemaining || 0) + leave.days,
            });
        }
      } else if (type === "rejected") {
          updateData.status = "rejected";
          updateData.approvalCancelled = false;
          updateData.managerSignature = ""; 
          toastMsg = "已駁回";
      }

      await updateDoc(
        doc(db, "artifacts", APP_ID, "public", "data", "leaves", leaveId),
        updateData
      );

      showToast(toastMsg);
      await notify(leaveId, type === "proxy" ? "proxy" : "decision");
    } catch (e) {
      alert("操作失敗: " + e.message);
    }
  };

  return (
    <div className="paper-page leave-view p-4 pb-24 relative">
      <div className="page-actions flex justify-between items-center mb-4 gap-2">
        <h2 className="text-base font-bold flex items-center gap-1.5 whitespace-nowrap shrink-0">
          <FileText size={18} className="text-blue-600" /> 請假
        </h2>
        <div className="lake-segment flex gap-1.5 shrink-0" role="group" aria-label="請假檢視">
          <button
            onClick={() => setViewMode("list")}
            aria-pressed={viewMode === "list"}
            className={`px-2.5 py-1.5 rounded-lg shadow-sm font-bold text-[11px] flex items-center gap-1 transition-all whitespace-nowrap ${
              viewMode === "list"
                ? "lake-tab-active bg-blue-600 text-white ring-2 ring-blue-200"
                : "bg-white text-slate-600 border border-slate-200"
            }`}
          >
            <List size={14} /> 列表
          </button>
          <button
            onClick={() => setViewMode("calendar")}
            aria-pressed={viewMode === "calendar"}
            className={`px-2.5 py-1.5 rounded-lg shadow-sm font-bold text-[11px] flex items-center gap-1 transition-all whitespace-nowrap ${
              viewMode === "calendar"
                ? "lake-tab-active bg-purple-600 text-white ring-2 ring-purple-200"
                : "bg-white text-slate-600 border border-slate-200"
            }`}
          >
            <CalIcon size={14} /> 行事曆
          </button>
          <button
            aria-pressed={viewMode === "form"}
            onClick={() => {
              setViewMode("form");
              setIsEditing(false);
              setEditTargetUid(null);
              setInitialFormData(null);
            }}
            className={`px-2.5 py-1.5 rounded-lg shadow-sm font-bold text-[11px] flex items-center gap-1 transition-all whitespace-nowrap ${
              viewMode === "form"
                ? "lake-tab-active bg-green-600 text-white ring-2 ring-green-200"
                : "bg-white text-slate-600 border border-slate-200"
            }`}
          >
            <Plus size={14} /> 新增
          </button>
        </div>
      </div>

      {viewMode === "calendar" && (
        <LeaveCalendar
          leaves={leaves}
          role={role}
          user={user}
          users={users} // [新增] 傳遞 users 資料給行事曆顯示生日
          onDelete={handleDelete}
          onEdit={handleEdit}
        />
      )}

      {viewMode === "form" && (
        <LeaveForm
          proxyEmails={proxyEmails}
          proxyOptions={proxyOptions}
          proxyOptionsLoading={proxyOptionsLoading}
          isAdminLeave={role === ROLES.ADMIN && (!isEditing || editTargetUid === user.uid)}
          user={user}
          userProfile={userProfile}
          targetAnnualInfo={targetAnnualInfo}
          leaves={leaves}
          editTargetUid={editTargetUid}
          editId={editId}
          onSubmit={handleFormSubmit}
          initialData={initialFormData}
          isEditing={isEditing}
          onCancel={() => setViewMode("list")}
        />
      )}

      {viewMode === "list" && (
        <LeaveList
          proxyEmails={proxyEmails}
          proxyOptions={proxyOptions}
          onNotify={notify}
          leaves={leaves}
          role={role}
          user={user}
          onDelete={handleDelete}
          onSign={handleSign}
          onEdit={handleEdit}
        />
      )}

    </div>
  );
}
